import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  Chip,
  Rating
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsIcon from '@mui/icons-material/Directions';
import StarIcon from '@mui/icons-material/Star';
import { searchEmergencyServices } from './utils/googleMapsLoader';

/**
 * HospitalSearch Component - Allows searching for nearby hospitals by location
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Function to close the dialog
 * @param {Function} onSelect - Function to select a hospital
 * @param {string} initialLocation - Initial location to search for
 * @returns {JSX.Element} - Rendered component
 */
const HospitalSearch = ({ open, onClose, onSelect, initialLocation = '' }) => {
  const [location, setLocation] = useState(initialLocation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const theme = useTheme();

  const handleSearch = async () => {
    if (!location.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use Google Maps Places API to search for nearby hospitals
      const results = await searchEmergencyServices(location, 'hospital', 15000); // 15km radius
      setHospitals(results);
      setSelectedHospital(null);
    } catch (err) {
      console.error('Error fetching hospitals:', err);
      setError('Failed to fetch hospitals. Please check your location and try again.');
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHospital = (hospital) => {
    setSelectedHospital(hospital);
  };

  const handleConfirmSelection = () => {
    if (selectedHospital) {
      onSelect(selectedHospital);
      onClose();
    }
  };



  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Find Nearby Hospitals</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Enter address, city, or zip code"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch}
            disabled={loading || !location.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Search'}
          </Button>
        </Box>
        
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        {hospitals.length > 0 && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : '#f5f5f5'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Nearby Hospitals ({hospitals.length} found)
            </Typography>
            <List>
              {hospitals.map((hospital) => (
                <React.Fragment key={hospital.id}>
                  <ListItem 
                    button 
                    onClick={() => handleSelectHospital(hospital)}
                    selected={selectedHospital && selectedHospital.id === hospital.id}
                    sx={{
                      bgcolor: selectedHospital && selectedHospital.id === hospital.id 
                        ? (theme.palette.mode === 'dark' ? 'rgba(66, 66, 77, 0.8)' : '#e3f2fd') 
                        : 'transparent',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemIcon>
                      <LocalHospitalIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {hospital.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={`${hospital.distance} km`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            {hospital.rating && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Rating 
                                  value={hospital.rating} 
                                  readOnly 
                                  size="small" 
                                  precision={0.1}
                                />
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  ({hospital.userRatingsTotal || 0})
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>
                            <LocationOnIcon fontSize="small" sx={{ mr: 0.5, mt: 0.2 }} />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {hospital.address}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">{hospital.phoneNumber}</Typography>
                          </Box>
                          {hospital.website && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <DirectionsIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography 
                                variant="body2" 
                                component="a" 
                                href={hospital.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                Visit Website
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <Button 
                      startIcon={<DirectionsIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://maps.google.com/maps?q=${encodeURIComponent(hospital.address)}`, '_blank');
                      }}
                    >
                      Directions
                    </Button>
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleConfirmSelection}
          disabled={!selectedHospital}
          variant="contained"
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HospitalSearch;
